import { useEffect, useState } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import { X } from "@phosphor-icons/react";
import type { FigurinhaDaBiblioteca, TipoDeFigurinha } from "@foto/shared";
import { trpc } from "../../lib/trpc.js";
import { Sobreposicao } from "../../components/Sobreposicao.js";
import { Botao, Entrada, Falha, Girando, Segmentos, Vazio, cx } from "../../components/ui.js";

type Aba = "todas" | TipoDeFigurinha;

const ABAS: readonly { id: Aba; rotulo: string }[] = [
  { id: "todas", rotulo: "Todas" },
  { id: "figurinha", rotulo: "Figurinhas" },
  { id: "logo", rotulo: "Logos" },
  { id: "texto", rotulo: "Letras" },
];

const PAUSA_DA_BUSCA_MS = 300;

/** Onde se envia imagem nova: a página da biblioteca, que o editor abre em outra aba para não perder a edição. */
export const ROTA_DA_BIBLIOTECA = "/figurinhas";

/**
 * A biblioteca de figurinhas, logos e artes de letras, para escolher uma e colocá-la na foto.
 *
 * Só **lista e usa**: enviar e editar imagens é da página da biblioteca, que abre em outra aba — o operador
 * não perde a edição da foto para subir um logo. Ao voltar, a lista se refaz sozinha (a aba ganha foco) ou
 * pelo botão "Atualizar".
 *
 * As imagens são recortes com transparência: o fundo xadrez é o que mostra onde acaba a figurinha. Só aqui —
 * na foto, a figurinha fica sobre a foto.
 */
export function SeletorDeFigurinhas({
  aoEscolher,
  aoFechar,
  recusa,
}: {
  aoEscolher: (figurinha: FigurinhaDaBiblioteca) => void;
  aoFechar: () => void;
  /**
   * Por que esta imagem não pode entrar na foto (a foto ou a folha chegou ao teto de camadas, ou ao de figurinhas
   * diferentes), ou `null` se pode. As que não podem ficam desabilitadas, e o primeiro motivo aparece no alto.
   */
  recusa: (figurinha: FigurinhaDaBiblioteca) => string | null;
}) {
  const [aba, setAba] = useState<Aba>("todas");
  const [busca, setBusca] = useState("");
  const [buscaAplicada, setBuscaAplicada] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setBuscaAplicada(busca.trim()), PAUSA_DA_BUSCA_MS);
    return () => clearTimeout(t);
  }, [busca]);

  const consulta = trpc.figurinhas.list.useQuery(
    {
      ...(aba !== "todas" ? { tipo: aba } : {}),
      ...(buscaAplicada ? { busca: buscaAplicada } : {}),
      limite: 300,
    },
    {
      // Trocar de aba mantém a lista antiga até a nova chegar: sem isso a grade piscaria em branco.
      placeholderData: keepPreviousData,
      // Quem volta da aba onde enviou uma imagem nova a encontra aqui sem apertar nada.
      staleTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
    },
  );
  const itens = consulta.data ?? [];
  const filtrando = aba !== "todas" || buscaAplicada !== "";
  const motivos = itens.map((f) => recusa(f));
  const primeiroMotivo = motivos.find((m) => m !== null) ?? null;

  return (
    <Sobreposicao rotulo="Escolher figurinha ou logo" aoFechar={aoFechar}>
      <div className="flex flex-col gap-4 p-4">
        <header className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-[1.375rem] leading-tight">Figurinha ou logo</h2>
            <p className="mt-1 text-[0.8125rem] leading-relaxed text-texto-suave">
              Toque numa imagem para colocá-la no meio da foto. Depois é só arrastar.
            </p>
          </div>
          <button
            type="button"
            onClick={aoFechar}
            aria-label="Fechar"
            className="inline-flex size-11 shrink-0 items-center justify-center rounded-peca text-texto-suave hover:bg-superficie hover:text-texto"
          >
            <X size={20} aria-hidden />
          </button>
        </header>

        <div className="flex flex-col gap-3">
          <div className="-mx-1 overflow-x-auto px-1">
            <Segmentos
              rotulo="Tipo de imagem"
              valor={aba}
              opcoes={ABAS}
              aoMudar={setAba}
              className="min-w-max"
            />
          </div>
          <Entrada
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar pelo nome ou pela descrição"
            aria-label="Buscar figurinha"
            autoComplete="off"
          />
          <div className="flex flex-wrap items-center gap-2">
            <a
              href={ROTA_DA_BIBLIOTECA}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-11 items-center justify-center rounded-peca border border-borda bg-superficie-alta px-4 text-[0.9375rem] text-texto transition-colors hover:bg-borda"
            >
              Enviar imagem nova
            </a>
            <Botao
              tom="quieto"
              className="min-h-11"
              carregando={consulta.isFetching}
              onClick={() => void consulta.refetch()}
            >
              Atualizar
            </Botao>
            <span className="text-[0.75rem] text-texto-fraco">
              A página de envio abre em outra aba: sua foto continua aqui.
            </span>
          </div>
        </div>

        {primeiroMotivo && (
          <p role="status" className="text-[0.8125rem] leading-relaxed text-descartar">
            {primeiroMotivo}
          </p>
        )}

        {consulta.isError && !consulta.data ? (
          <Falha
            mensagem={consulta.error.message}
            tentarNovamente={() => void consulta.refetch()}
          />
        ) : consulta.isLoading ? (
          <Girando className="mx-auto my-10 size-6 text-texto-fraco" />
        ) : itens.length === 0 ? (
          <Vazio
            compacto
            titulo={filtrando ? "Nada com esse filtro" : "Sua biblioteca está vazia"}
            descricao={
              filtrando
                ? "Tente outra palavra, ou veja todas as imagens."
                : "Envie figurinhas e logos com fundo transparente (PNG ou WebP) para usar nas fotos."
            }
            acao={
              filtrando ? (
                <Botao
                  tom="quieto"
                  onClick={() => {
                    setAba("todas");
                    setBusca("");
                  }}
                >
                  Ver todas
                </Botao>
              ) : (
                <a
                  href={ROTA_DA_BIBLIOTECA}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-11 items-center justify-center rounded-peca bg-brasa px-4 text-[0.9375rem] font-semibold text-fundo hover:bg-brasa-forte"
                >
                  Enviar a primeira imagem
                </a>
              )
            }
          />
        ) : (
          <ul
            className={cx(
              "grid grid-cols-3 gap-2 sm:grid-cols-4",
              consulta.isFetching && consulta.isPlaceholderData && "opacity-60",
            )}
          >
            {itens.map((f, i) => (
              <li key={f.id}>
                <button
                  type="button"
                  disabled={motivos[i] !== null}
                  onClick={() => aoEscolher(f)}
                  aria-label={`Colocar ${f.nome} na foto`}
                  title={motivos[i] ?? f.descricao ?? f.nome}
                  className="group block w-full text-left disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="fundo-xadrez relative block aspect-square overflow-hidden rounded-peca border-2 border-transparent group-hover:border-borda-forte group-focus-visible:border-brasa">
                    <img
                      src={f.urls.mini}
                      alt=""
                      loading="lazy"
                      draggable={false}
                      className="absolute inset-0 size-full object-contain p-1.5"
                    />
                  </span>
                  <span className="mt-1 block truncate text-[0.75rem] text-texto-suave">
                    {f.nome}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Sobreposicao>
  );
}
